import { useState, useEffect } from "react";
import { DollarSign, TrendingUp, Calendar, Receipt, FileText, ArrowUpRight, Download, RotateCw, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";
import { RevShareStatusCard } from "@/components/admin/RevShareStatusCard";
import { SetupWarning } from "@/components/ui/setup-warning";
import { SectionInfo } from "@/components/ui/section-info";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { handleApproveWithFlag } from "@/components/admin/revenueCollectionActions";

interface BillingRecord {
  id: string;
  client_id: string;
  client_name: string;
  billing_week_start: string;
  billing_week_end: string;
  demos_delivered: number;
  billable_demos: number;
  demo_revenue_cents: number;
  revenue_share_cents: number;
  total_amount_cents: number;
  fanbasis_status: string | null;
  is_setup_fee?: boolean;
  created_at: string;
}

interface MonthlyRevShareRecord {
  id: string;
  client_name: string;
  month_start: string;
  attributed_revenue_cents: number;
  rev_share_cents: number;
  fanbasis_status: string | null;
}

export function RevenueCollectionTab({ dataVersion = 0 }: { dataVersion?: number }) {
  const [billingRecords, setBillingRecords] = useState<BillingRecord[]>([]);
  const [monthlyRevShare, setMonthlyRevShare] = useState<MonthlyRevShareRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [vhTeamCount, setVhTeamCount] = useState<number | null>(null);
  // Phase 11 Plan 05 (D-11, PAY-05): release.billing_v2 gates direct-charge approval path
  const billingV2 = useFeatureFlag('release.billing_v2');

  useEffect(() => {
    fetchBillingData();
  }, [dateRange, dataVersion]);

  useEffect(() => {
    supabase
      .from("vh_team_members")
      .select("id", { count: "exact", head: true })
      .eq("status", "active")
      .then(({ count }) => setVhTeamCount(count || 0));
  }, []);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case 'week':
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      // Fetch billing records with client names
      const { data: billing, error: billingError } = await supabase
        .from('weekly_billing')
        .select(`
          *,
          clients(company_name, setup_fee_cents)
        `)
        .gte('billing_week_start', startDate.toISOString().split('T')[0])
        .order('billing_week_start', { ascending: false });

      if (billingError) throw billingError;

      // Transform data
      const records: BillingRecord[] = (billing || []).map((b: any) => ({
        id: b.id,
        client_id: b.client_id,
        client_name: b.clients?.company_name || 'Unknown Client',
        billing_week_start: b.billing_week_start,
        billing_week_end: b.billing_week_end,
        demos_delivered: b.demos_delivered || 0,
        billable_demos: b.billable_demos || 0,
        demo_revenue_cents: b.demo_revenue_cents || 0,
        revenue_share_cents: b.revenue_share_cents || 0,
        total_amount_cents: b.total_amount_cents || 0,
        fanbasis_status: b.fanbasis_status,
        is_setup_fee: b.metadata?.is_setup_fee || false,
        created_at: b.created_at,
      }));

      setBillingRecords(records);

      // Fetch monthly rev share records
      const { data: monthly } = await supabase
        .from('monthly_rev_share')
        .select('*, clients(company_name)')
        .gte('month_start', startDate.toISOString().split('T')[0])
        .order('month_start', { ascending: false });

      setMonthlyRevShare(
        (monthly || []).map((m: any) => ({
          id: m.id,
          client_name: m.clients?.company_name || 'Unknown',
          month_start: m.month_start,
          attributed_revenue_cents: m.attributed_revenue_cents || 0,
          rev_share_cents: m.rev_share_cents || 0,
          fanbasis_status: m.fanbasis_status,
        }))
      );
    } catch (error) {
      console.error('Error fetching billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totalDemoRevenue = billingRecords
    .filter(r => !r.is_setup_fee)
    .reduce((sum, r) => sum + r.demo_revenue_cents, 0);
  
  const totalSetupFees = billingRecords
    .filter(r => r.is_setup_fee)
    .reduce((sum, r) => sum + r.total_amount_cents, 0);
  
  const totalRevenueShare = billingRecords
    .reduce((sum, r) => sum + r.revenue_share_cents, 0)
    + monthlyRevShare.reduce((sum, m) => sum + m.rev_share_cents, 0);
  
  const totalCollected = billingRecords
    .filter(r => r.fanbasis_status === 'succeeded')
    .reduce((sum, r) => sum + r.total_amount_cents, 0);
  
  const totalPending = billingRecords
    .filter(r => r.fanbasis_status === 'pending' || !r.fanbasis_status)
    .reduce((sum, r) => sum + r.total_amount_cents, 0);
  
  const totalFailed = billingRecords
    .filter(r => r.fanbasis_status === 'failed')
    .reduce((sum, r) => sum + r.total_amount_cents, 0);

  const getStatusBadge = (status: string | null, isSetupFee: boolean) => {
    if (isSetupFee) {
      return <BadgeVenture variant="info"><Receipt className="w-3 h-3 mr-1" />Setup Fee</BadgeVenture>;
    }
    switch (status) {
      case 'succeeded':
        return <BadgeVenture variant="success">Collected</BadgeVenture>;
      case 'pending':
        return <BadgeVenture variant="warning">Pending</BadgeVenture>;
      case 'failed':
        return <BadgeVenture variant="error">Failed</BadgeVenture>;
      default:
        return <BadgeVenture variant="default">Scheduled</BadgeVenture>;
    }
  };

  const formatCurrency = (cents: number) => {
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleExport = () => {
    if (billingRecords.length === 0) return;
    const headers = ["Client", "Period Start", "Period End", "Demos Delivered", "Billable Demos", "Demo Revenue", "Rev Share", "Total", "Status"];
    const rows = billingRecords.map(r => [
      r.client_name,
      r.billing_week_start,
      r.billing_week_end,
      r.demos_delivered,
      r.billable_demos,
      (r.demo_revenue_cents / 100).toFixed(2),
      (r.revenue_share_cents / 100).toFixed(2),
      (r.total_amount_cents / 100).toFixed(2),
      r.fanbasis_status || "scheduled",
    ]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `billing-${dateRange}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRetryCharge = async (record: BillingRecord) => {
    toast.info(`Retrying charge for ${record.client_name}...`);
    try {
      // Phase 11 Plan 05: branch on release.billing_v2 — flag ON goes through
      // fanbasis-direct-charge (D-11, PAY-05); flag OFF preserves legacy path.
      await handleApproveWithFlag(
        {
          client_id: record.client_id,
          amount_cents: record.total_amount_cents,
          application_fee_cents: record.revenue_share_cents ?? record.total_amount_cents,
          period: record.billing_week_start,
          billing_week_start: record.billing_week_start,
          description: `Revenue share — ${record.billing_week_start}`,
        },
        billingV2,
      );
      fetchBillingData();
    } catch {
      toast.error("Retry failed — check billing logs");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Revenue Collection</h2>
          <p className="text-sm text-muted-foreground">Track demo revenue, setup fees, and collections</p>
        </div>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as any)}
            className="px-3 py-2 border border-border rounded-lg bg-surface text-sm"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
            <option value="year">Last Year</option>
          </select>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {vhTeamCount === 0 && (
        <SetupWarning message="No Venture Hills setters or closers added yet — the system can't identify which deals are Venture Hills-attributed until team members are listed in Sales Operations." />
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <DollarSign className="w-5 h-5 mx-auto text-green-500 mb-2" />
            <p className="text-xl font-bold text-green-500">{formatCurrency(totalCollected)}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">Collected</p>
              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent className="max-w-xs"><p>Total successfully charged to clients via Fanbasis</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <TrendingUp className="w-5 h-5 mx-auto text-secondary mb-2" />
            <p className="text-xl font-bold">{formatCurrency(totalDemoRevenue)}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">Demo Revenue</p>
              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent className="max-w-xs"><p>Revenue from billable demos (after free demo threshold)</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <Receipt className="w-5 h-5 mx-auto text-blue-500 mb-2" />
            <p className="text-xl font-bold">{formatCurrency(totalSetupFees)}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">Setup Fees</p>
              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent className="max-w-xs"><p>One-time setup fees charged to clients</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <ArrowUpRight className="w-5 h-5 mx-auto text-purple-500 mb-2" />
            <p className="text-xl font-bold">{formatCurrency(totalRevenueShare)}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">Revenue Share</p>
              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent className="max-w-xs"><p>Monthly 7.5% charge on Venture Hills-attributed revenue for clients above $50K threshold</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <Calendar className="w-5 h-5 mx-auto text-amber-500 mb-2" />
            <p className="text-xl font-bold text-amber-500">{formatCurrency(totalPending)}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">Pending</p>
              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent className="max-w-xs"><p>Charges scheduled but not yet processed</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="text-center py-4">
            <FileText className="w-5 h-5 mx-auto text-red-500 mb-2" />
            <p className="text-xl font-bold text-red-500">{formatCurrency(totalFailed)}</p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-xs text-muted-foreground">Failed</p>
              <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild><Info className="w-3 h-3 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent className="max-w-xs"><p>Charges that failed — retry available per record</p></TooltipContent></Tooltip></TooltipProvider>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      <RevShareStatusCard />

      {/* Billing Records Table */}
      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Billing History</VentureCardTitle>
          <SectionInfo description="Weekly demo charges and monthly revenue share across all clients." />
        </VentureCardHeader>
        <VentureCardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading billing data...</p>
            </div>
          ) : billingRecords.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No billing records for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Demos</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Demo Revenue</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Rev Share</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billingRecords.map((record) => (
                    <tr key={record.id} className="border-b border-border hover:bg-surface transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-medium">{record.client_name}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {format(new Date(record.billing_week_start), 'MMM d')} - {format(new Date(record.billing_week_end), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {record.is_setup_fee ? '—' : `${record.billable_demos} / ${record.demos_delivered}`}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {record.is_setup_fee ? '—' : formatCurrency(record.demo_revenue_cents)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {record.revenue_share_cents > 0 ? formatCurrency(record.revenue_share_cents) : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold">
                        {formatCurrency(record.total_amount_cents)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(record.fanbasis_status, record.is_setup_fee || false)}
                          {record.fanbasis_status === 'failed' && (
                            <Button variant="ghost" size="sm" onClick={() => handleRetryCharge(record)} className="text-xs">
                              <RotateCw className="w-3 h-3 mr-1" />
                              Retry
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
